import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";

const KEY = "concept_guides";

export type GuideStep = {
  title: string;
  body?: string;
};

export type ConceptGuide = {
  id: string;
  title: string;
  conceptId?: string;
  description: string;
  imageUrls: string[];
  videoUrls?: string[];
  // Backward-compatible additions (optional). Old rows render without
  // these and the dialog defaults them to empty.
  dept?: string;
  tags?: string[];
  steps?: GuideStep[];
  // Product types the guide applies to besides its concept (decision 0128):
  // whole product lines (`idphoto`) and single packages (`cv_pro_makeup`).
  // Absent on guides written before 0124. On save the server keeps the stored
  // value of a key the payload leaves out; only an explicit [] clears it.
  lineCodes?: string[];
  productCodes?: string[];
};

export function useConceptGuides() {
  return useQuery({
    queryKey: ["settings", KEY],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/settings/{key}", {
        params: { path: { key: KEY } },
      });
      if (response.status === 404) return [] as ConceptGuide[];
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được hướng dẫn concept"));
      // Setting `value` is freeform JSON — settings written by an older
      // FE may be an object/string. Coerce non-arrays to [] so the view
      // doesn't crash on .slice / .map.
      const v = data.value;
      return Array.isArray(v) ? (v as ConceptGuide[]) : [];
    },
    staleTime: 60_000,
  });
}

export function useSaveConceptGuides() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: ConceptGuide[]) => {
      const { error } = await api.PUT("/settings/{key}", {
        params: { path: { key: KEY } },
        body: { value: items },
      });
      if (error) throw new Error(serverErrorMessage(error, "Không lưu được hướng dẫn concept"));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["settings", KEY] }),
  });
}
